/**
 * The outbox.
 *
 * **This is the most important file in the application.** A bank terminal
 * approves locally: the card is charged on the counter by hardware that does
 * not know the internet exists. Between the approval and the server
 * acknowledging the order, the only record of that money is here.
 *
 * So the rule, from `docs/offline.md`:
 *
 *   1. `shell_terminal_pay` returns. Write the claim and **await the write**
 *      before anything else — before the success screen, before the receipt,
 *      before the POST.
 *   2. The receipt prints from local data. It does not need the server.
 *   3. The order POSTs. On success the entry is marked acknowledged.
 *   4. It is **never deleted on the kiosk's own authority.** Only a server
 *      acknowledgement clears it.
 *
 * A silent drop here is a customer charged for a coffee nobody made.
 *
 * ── The state nobody had thought of ──────────────────────────────────────
 *
 * Rule 1 used to run only on an approval, because the shell answered with a
 * boolean and a timeout was `false`. But a terminal that stopped answering may
 * still have charged the card, so `false` covered two different amounts of
 * money and the ambiguous half was written nowhere.
 *
 * `unresolved` is that half. It is money we **may** hold, so:
 *
 *   - it is written and awaited exactly like an approval;
 *   - it is **never posted automatically**, because claiming a payment we may
 *     not have taken is the mirror of losing one we did;
 *   - it sits on the recovery banner until a person checks the terminal's own
 *     batch and says which it was.
 */
import { openDB } from 'idb'
import type { DBSchema, IDBPDatabase } from 'idb'
import type { CreateOrderInput } from '@mgc/protocol'

export interface OutboxEntry {
  /** The payment id, and the server's idempotency key. */
  readonly id: string
  readonly orderId: string
  readonly method: 'cash' | 'card' | 'qpay'
  /** Minor units, as a string — IndexedDB structured-clones a bigint, but a
   *  string survives an export, a log line and a support screenshot. */
  readonly amount: string
  readonly reference: string | null
  readonly detail: Record<string, string> | null
  readonly capturedAt: string
  /** The whole order, so a replay needs nothing else that might have been lost. */
  readonly order: SerialisedOrder
  readonly state: OutboxState
  readonly rejection: string | null
  readonly attempts: number
  readonly lastTriedAt: string | null
  /** Who resolved an `unresolved` or `rejected` entry, and why. */
  readonly resolution: { readonly by: string; readonly reason: string; readonly at: string } | null
}

/**
 * Where a claim is in its life.
 *
 * - `claimed`      — money was taken. Replay until the server accepts it.
 * - `unresolved`   — money **may** have been taken; the terminal did not say.
 *                    Never replayed on its own; a person decides.
 * - `acknowledged` — the server has it. The only state that may be pruned.
 * - `rejected`     — the server refused it. A person must deal with it.
 * - `written-off`  — a person looked and said no money was taken. Not a
 *                    deletion: the row and who decided it both survive.
 */
export type OutboxState = 'claimed' | 'unresolved' | 'acknowledged' | 'rejected' | 'written-off'

/** Entries a person has to act on, as opposed to ones that replay themselves. */
export const needsAPerson = (entry: OutboxEntry): boolean => entry.state === 'unresolved' || entry.state === 'rejected'

/** `CreateOrderInput` with every `Money` as a decimal string. */
export interface SerialisedOrder {
  readonly id: string
  readonly orderType: 'eat_in' | 'take_out'
  readonly lines: readonly {
    id: string
    productId: string
    quantity: number
    temperature?: 'hot' | 'ice'
    changeCode?: string
    addCodes: string[]
    orderType?: 'eat_in' | 'take_out'
    note?: string
  }[]
  readonly claimed: { total: string; lines: readonly { id: string; total: string }[] }
  readonly company: { registrationNumber: string; name: string; tin?: string } | null
  /** Absent from entries written before coupons existed, which is also «none». */
  readonly coupon?: string
  readonly note?: string
  readonly placedAt: string
}

interface KioskDb extends DBSchema {
  outbox: { key: string; value: OutboxEntry; indexes: { state: string } }
  menu: { key: string; value: { branchId: string; revision: string; servedAt: string; document: unknown } }
  meta: { key: string; value: unknown }
}

const NAME = 'mgc-kiosk'
/** 2 added `resolution` and the `unresolved` / `written-off` states. */
const VERSION = 2

let handle: Promise<IDBPDatabase<KioskDb>> | undefined

function db(): Promise<IDBPDatabase<KioskDb>> {
  handle ??= openDB<KioskDb>(NAME, VERSION, {
    // Idempotent per store rather than "create everything", because this now
    // runs against databases that already exist. A `createObjectStore` for a
    // store that is already there throws, and it throws *inside* the upgrade
    // transaction — which leaves the database unopenable rather than merely
    // unmigrated, on a machine holding a day's payments.
    upgrade(database) {
      if (!database.objectStoreNames.contains('outbox')) {
        const outbox = database.createObjectStore('outbox', { keyPath: 'id' })
        outbox.createIndex('state', 'state')
      }
      if (!database.objectStoreNames.contains('menu')) database.createObjectStore('menu')
      if (!database.objectStoreNames.contains('meta')) database.createObjectStore('meta')
    }
  })
  return handle
}

/** Convert an order to the serialisable form. `Money` is a bigint; JSON is not. */
export function serialiseOrder(input: CreateOrderInput): SerialisedOrder {
  return {
    id: input.id,
    orderType: input.orderType,
    lines: input.lines.map((l) => ({
      id: l.id,
      productId: l.productId,
      quantity: l.quantity,
      ...(l.temperature === undefined ? {} : { temperature: l.temperature }),
      ...(l.changeCode === undefined ? {} : { changeCode: l.changeCode }),
      addCodes: [...l.addCodes],
      ...(l.orderType === undefined ? {} : { orderType: l.orderType }),
      ...(l.note === undefined ? {} : { note: l.note })
    })),
    claimed: {
      total: input.claimed.total.toString(),
      lines: input.claimed.lines.map((l) => ({ id: l.id, total: l.total.toString() }))
    },
    company: input.company,
    ...(input.coupon === undefined ? {} : { coupon: input.coupon }),
    ...(input.note === undefined ? {} : { note: input.note }),
    placedAt: input.placedAt
  }
}

/** And back, for the replay. */
export function deserialiseOrder(stored: SerialisedOrder, entry: OutboxEntry): CreateOrderInput {
  return {
    id: stored.id,
    orderType: stored.orderType,
    lines: stored.lines.map((l) => ({ ...l, addCodes: l.addCodes })),
    claimed: {
      total: BigInt(stored.claimed.total) as never,
      lines: stored.claimed.lines.map((l) => ({ id: l.id, total: BigInt(l.total) as never }))
    },
    company: stored.company,
    ...(stored.coupon === undefined ? {} : { coupon: stored.coupon }),
    ...(stored.note === undefined ? {} : { note: stored.note }),
    placedAt: stored.placedAt,
    settledPayments: [
      {
        id: entry.id,
        method: entry.method,
        amount: BigInt(entry.amount) as never,
        ...(entry.reference === null ? {} : { reference: entry.reference }),
        ...(entry.detail === null ? {} : { detail: entry.detail }),
        capturedAt: entry.capturedAt
      }
    ]
  }
}

/**
 * Record that money was taken. **Await this before anything else.**
 *
 * `put` rather than `add` so a retry of the same approval is idempotent here as
 * well as on the server.
 */
export async function claim(
  entry: Omit<OutboxEntry, 'state' | 'rejection' | 'attempts' | 'lastTriedAt' | 'resolution'>,
  /** `true` when the terminal did not tell us whether it charged the card. */
  unresolved = false
): Promise<void> {
  const database = await db()
  const existing = await database.get('outbox', entry.id)
  if (existing !== undefined && existing.state === 'acknowledged') return
  await database.put('outbox', {
    ...entry,
    state: unresolved ? 'unresolved' : 'claimed',
    rejection: null,
    attempts: existing?.attempts ?? 0,
    lastTriedAt: existing?.lastTriedAt ?? null,
    resolution: existing?.resolution ?? null
  })
}

export async function acknowledge(id: string): Promise<void> {
  const database = await db()
  const entry = await database.get('outbox', id)
  if (entry === undefined) return
  await database.put('outbox', { ...entry, state: 'acknowledged', rejection: null })
}

/**
 * A person checked the terminal's batch: the money *was* taken.
 *
 * The entry becomes an ordinary claim and replays from here like any other.
 */
export async function confirmTaken(id: string, by: string): Promise<void> {
  const database = await db()
  const entry = await database.get('outbox', id)
  if (entry === undefined || entry.state === 'acknowledged') return
  await database.put('outbox', {
    ...entry,
    state: 'claimed',
    rejection: null,
    resolution: { by, reason: 'confirmed against the terminal', at: new Date().toISOString() }
  })
}

/**
 * A person checked and no money was taken — or took the loss knowingly.
 *
 * Deliberately **not** a delete. `docs/offline.md` says the outbox is bounded by
 * nothing because a bound is a data-loss policy; this is not a bound, it is a
 * decision somebody made and put their name to, and the row outlives it.
 */
export async function writeOff(id: string, by: string, reason: string): Promise<void> {
  const database = await db()
  const entry = await database.get('outbox', id)
  if (entry === undefined || entry.state === 'acknowledged') return
  await database.put('outbox', {
    ...entry,
    state: 'written-off',
    resolution: { by, reason, at: new Date().toISOString() }
  })
}

/**
 * Mark a claim the server refused.
 *
 * It stays in the store, and stays on the recovery banner, until a person deals
 * with it. A rejection is not a reason to forget that money was taken.
 */
export async function reject(id: string, rejection: string): Promise<void> {
  const database = await db()
  const entry = await database.get('outbox', id)
  if (entry === undefined) return
  await database.put('outbox', { ...entry, state: 'rejected', rejection })
}

export async function noteAttempt(id: string): Promise<void> {
  const database = await db()
  const entry = await database.get('outbox', id)
  if (entry === undefined) return
  await database.put('outbox', { ...entry, attempts: entry.attempts + 1, lastTriedAt: new Date().toISOString() })
}

/**
 * Everything still owed. The recovery banner reads this.
 *
 * `written-off` is settled — a person looked at it and decided — so it leaves
 * the banner while staying in the store. Everything else is outstanding,
 * including `unresolved`, which is the whole point of that state.
 */
export async function outstanding(): Promise<OutboxEntry[]> {
  const database = await db()
  const all = await database.getAll('outbox')
  return all.filter((e) => e.state !== 'acknowledged' && e.state !== 'written-off')
}

export async function depth(): Promise<number> {
  return (await outstanding()).length
}

/**
 * Drop acknowledged entries older than a week.
 *
 * The *only* deletion in this file, and it touches nothing unacknowledged: an
 * outbox bounded by anything other than "the server has it" is a data-loss
 * policy wearing a retention policy's clothes.
 */
export async function pruneAcknowledged(before: Date): Promise<number> {
  const database = await db()
  const all = await database.getAll('outbox')
  let removed = 0
  for (const entry of all) {
    if (entry.state !== 'acknowledged') continue
    if (new Date(entry.capturedAt) >= before) continue
    await database.delete('outbox', entry.id)
    removed += 1
  }
  return removed
}

// ── The menu cache ────────────────────────────────────────────────────────

export async function saveMenu(branchId: string, revision: string, servedAt: string, document: unknown): Promise<void> {
  const database = await db()
  await database.put('menu', { branchId, revision, servedAt, document }, 'current')
}

/**
 * The cached menu, if it belongs to the branch this kiosk is enrolled against.
 *
 * It is stored under one key and survives a re-enrolment, so a kiosk moved
 * between shops — or a bench device paired to a second branch — used to boot
 * offline onto the previous shop's catalogue. Harmless while every shop shared
 * one catalogue; a wrong-price bug the moment they do not.
 */
export async function loadMenu(
  branchId: string
): Promise<{ branchId: string; revision: string; servedAt: string; document: unknown } | undefined> {
  const database = await db()
  const cached = await database.get('menu', 'current')
  if (cached === undefined) return undefined
  if (cached.branchId !== branchId) {
    // Another shop's menu. Drop it rather than keep something we will never
    // serve; the next online load replaces it.
    await database.delete('menu', 'current')
    return undefined
  }
  return cached
}

/**
 * Drop the cached menu.
 *
 * Called when the held document fails to parse — a kiosk that went offline
 * holding a menu an older deploy wrote has no other way to notice, and serving
 * from a document nothing agrees about is worse than having none.
 */
export async function forgetMenu(): Promise<void> {
  const database = await db()
  await database.delete('menu', 'current')
}

// ── Small durable facts ───────────────────────────────────────────────────

export async function setMeta(key: string, value: unknown): Promise<void> {
  const database = await db()
  await database.put('meta', value, key)
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const database = await db()
  return (await database.get('meta', key)) as T | undefined
}

/**
 * Close the connection.
 *
 * An open connection blocks `deleteDatabase` and blocks a version upgrade, so
 * dropping the module-level handle without closing it leaves a database nothing
 * can delete and nothing can migrate — which is a hang, not an error.
 */
export async function close(): Promise<void> {
  const open = handle
  handle = undefined
  if (open === undefined) return
  try {
    ;(await open).close()
  } catch {
    /* already gone */
  }
}
